import { ITIN_UI, ITIN_TPL, ITIN_HIGHLIGHTS } from "@/data/itinerary";
import { toHijri, addDays } from "@/lib/hijri";
import { readCorrection } from "@/lib/manasikReminders";
import { getStations } from "@/lib/manasikContent";
import { scheduleReminders, enablePush } from "@/lib/push";
import { getLearn, putLearn } from "@/lib/learnSync";
import { fill } from "@/lib/utils";

// Personal itinerary: settings {trip, depart, ret, first, switchDate, remind, remindTime, t} stored in
// localStorage + account (learn.itinerary). The plan maps day templates (and the 8–13 Dhul-Hijjah stations
// for Hajj trips) onto the pilgrim's real travel dates; push kind "itinerary" = one morning reminder per day.
export const ITIN_KEY = "itinerary";
export const MAX_DAYS = 60;
const DAY_MS = 86400000;

export const readItinerary = () => { try { return JSON.parse(localStorage.getItem(ITIN_KEY)) || null; } catch { return null; } };
export const writeItinerary = (s) => { if (s) localStorage.setItem(ITIN_KEY, JSON.stringify(s)); else localStorage.removeItem(ITIN_KEY); return s; };

export const parseDate = (ymd) => { const [y, m, d] = String(ymd).split("-").map(Number); return new Date(y, m - 1, d, 12); };
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const noon = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
export const daysBetween = (a, b) => Math.round((noon(b) - noon(a)) / DAY_MS);

// Default city-switch date: roughly a third of the trip in the first city (min 2, max 5 days).
export function defaultSwitch(depart, ret, first) {
  const total = daysBetween(parseDate(depart), parseDate(ret));
  const firstLen = Math.min(5, Math.max(2, Math.round(total / 3)));
  return ymd(addDays(parseDate(depart), first === "madinah" ? firstLen : total - firstLen));
}

export function validate(s) {
  const d = parseDate(s.depart), r = parseDate(s.ret), total = daysBetween(d, r);
  if (!(total >= 1 && total <= MAX_DAYS)) return "errRange";
  const sw = parseDate(s.switchDate);
  if (!(sw > d && sw <= r)) return "errSwitch";
  return null;
}

export const cityOn = (s, date) => {
  const sw = parseDate(s.switchDate);
  const before = date < sw;
  return s.first === "madinah" ? (before ? "madinah" : "makkah") : (before ? "makkah" : "madinah");
};

const tplDay = (id, extraItems = []) => ({ tpl: id, title: ITIN_TPL[id].title, items: [...ITIN_TPL[id].items, ...extraItems] });

// Build the day list; `tc` is only needed for Hajj-station item labels.
export function buildItinerary(s, correction = readCorrection(), stations = getStations()) {
  if (!s || validate(s)) return [];
  const depart = parseDate(s.depart), ret = parseDate(s.ret);
  const total = daysBetween(depart, ret);
  const sw = parseDate(s.switchDate);
  const days = [];
  let hl = { madinah: 0, makkah: 0 };
  for (let i = 0; i <= total; i++) {
    const date = addDays(depart, i);
    const hijri = toHijri(date, correction);
    const city = cityOn(s, date);
    const isFirst = i === 0, isLast = i === total;
    const isSwitch = ymd(date) === ymd(sw);
    const dayBeforeSwitch = ymd(addDays(date, 1)) === ymd(sw);
    const hajjDay = s.trip === "hajj" && hijri.month === 12 && hijri.day >= 8 && hijri.day <= 13 ? hijri.day : null;
    let day;
    if (hajjDay) {
      const tlDay = hajjDay >= 11 ? "tasyrik" : String(hajjDay);
      const items = stations.filter((st) => st.day === tlDay).map((st) => ({ label: st.title, to: `/haji-timeline?day=${tlDay}`, station: st.id }));
      day = { ...tplDay(`hajj${hajjDay}`, items), city: hajjDay === 9 ? "arafah" : "mina", hajjDay, note: hajjDay === 13 ? ITIN_UI.nafarNote : null };
    } else if (isFirst) {
      day = { ...tplDay(s.first === "makkah" ? "departIhram" : "depart"), city: "travel" };
    } else if (isLast) {
      day = { ...tplDay("return"), city: "travel" };
    } else if (isSwitch) {
      day = city === "makkah" ? { ...tplDay("toMakkahUmrah"), city } : { ...tplDay("arriveMadinah"), city };
    } else if (i === 1 && !isSwitch) {
      day = city === "makkah" ? { ...tplDay("arriveMakkahUmrah"), city } : { ...tplDay("arriveMadinah"), city };
    } else if (dayBeforeSwitch || (i === total - 1)) {
      day = city === "makkah" ? { ...tplDay("makkahLast"), city } : { ...tplDay(dayBeforeSwitch ? "madinahLast" : "madinahLastHome"), city };
    } else if (s.trip === "hajj" && hijri.month === 12 && hijri.day === 7 && city === "makkah") {
      day = { ...tplDay("preHajj"), city };
    } else {
      const list = ITIN_HIGHLIGHTS[city];
      const pick = list[hl[city] % list.length]; hl[city] += 1;
      day = { ...tplDay(city === "makkah" ? "makkahDay" : "madinahDay", [pick]), city };
    }
    days.push({ ...day, n: i + 1, date, key: ymd(date), hijri });
  }
  return days;
}

export const hajjDaysCovered = (days) => days.some((d) => d.hajjDay);
// Hajj days scheduled while the plan says the pilgrim is still in Madinah → the switch date must move.
export const hajjCityConflict = (s, days) => days.some((d) => d.hajjDay && cityOn(s, d.date) !== "makkah");
export const cityCounts = (days) => days.reduce((acc, d) => { if (d.city === "madinah") acc.madinah += 1; else if (d.city !== "travel") acc.makkah += 1; return acc; }, { madinah: 0, makkah: 0 });

// Trip status relative to today: {phase: 'before'|'during'|'after', dayIndex, daysToGo}
export function tripStatus(s, days, now = new Date()) {
  if (!s || !days.length) return null;
  const toDepart = daysBetween(now, days[0].date);
  if (toDepart > 0) return { phase: "before", daysToGo: toDepart };
  const idx = days.findIndex((d) => d.key === ymd(now));
  if (idx >= 0) return { phase: "during", dayIndex: idx, day: days[idx] };
  return { phase: "after" };
}

// One push per remaining day at the chosen local time: "Day N: <title>" + agenda summary.
export function buildItineraryReminders(s, days, tc, now = new Date()) {
  if (!s?.remind) return [];
  const [hh, mm] = String(s.remindTime || "06:00").split(":").map(Number);
  const out = [];
  for (const d of days) {
    const at = new Date(d.date.getFullYear(), d.date.getMonth(), d.date.getDate(), hh, mm);
    if (at <= now) continue;
    out.push({
      send_at: at.toISOString(),
      title: fill(tc(ITIN_UI.remindTitle), { n: d.n, title: tc(d.title) }),
      body: d.items.slice(0, 3).map((it) => tc(it.label)).join(" · "),
      url: "/itinerary",
    });
  }
  return out;
}

let chain = Promise.resolve();
const serialize = (fn) => { const p = chain.then(fn, fn); chain = p.catch(() => {}); return p; };

// Save locally, mirror to the account and replace the server-side "itinerary" push queue.
export function saveItinerary(s, tc) {
  const next = s ? { ...s, t: Date.now() } : null;
  writeItinerary(next);
  return serialize(async () => {
    putLearn({ itinerary: next || { deleted: true, t: Date.now() } });
    const items = next ? buildItineraryReminders(next, buildItinerary(next), tc) : [];
    if (next?.remind) { try { await enablePush(); } catch { /* handled by pushOk */ } }
    const res = await scheduleReminders(items, "itinerary").catch(() => null);
    return { settings: next, scheduled: res?.scheduled ?? null, pushOk: res != null };
  });
}

// On app start: adopt newer account settings, then refresh the push queue for the remaining days.
export function renewItinerary(tc) {
  return serialize(async () => {
    const remote = (await getLearn())?.itinerary;
    const local = readItinerary();
    let s = local;
    if (remote && (remote.t || 0) > (local?.t || 0)) s = writeItinerary(remote.deleted ? null : remote);
    if (s?.remind) await scheduleReminders(buildItineraryReminders(s, buildItinerary(s), tc), "itinerary").catch(() => null);
    return s;
  });
}
